import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { browserHistory } from 'edrolo/utils/browserHistory';
import { withHook } from 'core/hocs';
import lodashThrottle from 'lodash/throttle';

export const trackAnalytics = (type, payload) => {
  // These actions get picked up by edrolo/configureStore/analyticsMiddleware
  // See: https://github.com/markdalgleish/redux-analytics
  return {
    meta: {
      analytics: {
        type,
        payload,
      },
    },
  };
};

export const trackPageEvent = (pageName, attributes) => {
  global.analytics.page(pageName, attributes);

  if (global.Sprig) {
    global.Sprig('track', `viewed page: ${pageName}`, attributes);
  }
};

export const trackPageVisitHoc = (pageName, WrappedComponent, origin = '') => {
  const TrackPageVisit = (props) => {
    const { resetAnalyticsContext } = useAnalytics();
    useEffect(() => {
      trackPageEvent(pageName, { origin });

      // setup a listener on the browser history to reset the analytics context
      // each time the route changes. This is to ensure that attributes added
      // on one route don't follow along to the next route where they are not required
      const unlisten = browserHistory.listen(() => {
        resetAnalyticsContext();
      });
      return () => {
        unlisten();
      };
      /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, []);
    return <WrappedComponent {...props} />;
  };

  return TrackPageVisit;
};

/*
 * Track events to third-party services:
 * Segment - via global.analytics, loaded in _segment.html.
 * Appcues - via global.Appcues, loaded in _appcues.html.
 *
 * TODO
 * Implemented as part of the "time to value" initiative in 2023.03.
 * To save time, this function is only invoked by a handful of events.
 * Worth considering if this should be the de-facto way of logging
 * events to third-party services.
 */
export const track = (eventName, eventAttributes = {}) => {
  global.analytics.track(eventName, eventAttributes);

  // Appcues library is only loaded for verified teachers
  // We only fire Appcues events for verified teachers because we're not
  // currently intending to use Appcues for students.
  if (global.Appcues) {
    global.Appcues.track(eventName, eventAttributes);
  }

  if (global.Sprig) {
    global.Sprig('track', eventName, eventAttributes);
  }
};

const AnalyticsContext = React.createContext({
  eventAttributes: {},
  eventPrefix: '',
});

/**
 * This hook provides a context for tracking events with analytics.track()
 * It also provides some utilities for tracking events.
 *
 * The context is shared across the entire app, so it's important to
 * reset the context for each view or attributes added will persist across
 * urls. This has currently been implemented in the trackPageVisitHoc
 * function, which is loaded on all views.
 *
 * The context can be disabled by setting disableContext to true.
 *
 * @param {Object} options - The options for the hook.
 * @param {Object} options.eventAttributes - The attributes that will be added to all events across components for this rendered tree.
 * @param {string} options.eventPrefix - The prefix that will be added to all events.
 * @param {number|null} options.throttle - The maximum frequency in milliseconds for tracking events.
 * @param {boolean|null} options.disableContext - Instanciate tracking without context.
 * @returns {Object} - The tracking functions and context.
 */
export const useAnalytics = ({
  eventAttributes: initialEventAttributes = {},
  eventPrefix = '',
  throttle = null,
  disableContext = false,
} = {}) => {
  const analyticsContext = useContext(AnalyticsContext);

  /**
   * Track an event with the specified event name and attributes.
   *
   * @param {string} event - The name of the event.
   * @param {Object} eventAttributes - The attributes of the event.
   */
  const trackPrefixed = (event, eventAttributes = {}) => {
    const eventName = disableContext
      ? event
      : `${analyticsContext.eventPrefix}${event}`;
    track(eventName, eventAttributes);
  };

  /**
   * Throttles the trackPrefixed function if throttle is set.
   */
  const maybeThrottledTrack = useMemo(
    () =>
      throttle
        ? lodashThrottle(trackPrefixed, throttle, {
            leading: true,
            trailing: false,
          })
        : trackPrefixed,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [throttle, analyticsContext.eventPrefix]
  );

  // if throttle is set, cancel the throttled function on unmount
  useEffect(() => {
    return () => {
      if (throttle) {
        maybeThrottledTrack.cancel();
      }
    };
  }, [maybeThrottledTrack, throttle]);

  /**
   * Tracks an event with the specified event name and attributes.
   *
   * @param {string} event - The name of the event.
   * @param {Object} eventAttributes - The attributes of the event.
   */
  const trackEvent = useCallback(
    (event, eventAttributes = {}) => {
      const attributesWithContext = {
        ...analyticsContext.eventAttributes,
        ...eventAttributes,
      };

      maybeThrottledTrack(
        event,
        disableContext ? eventAttributes : attributesWithContext
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maybeThrottledTrack, disableContext]
  );

  /**
   * Adds event attributes to the context.
   *
   * @param {Object} eventAttributes - The event attributes to add.
   */
  const addEventAttributes = (eventAttributes = {}) => {
    analyticsContext.eventAttributes = {
      ...analyticsContext.eventAttributes,
      ...eventAttributes,
    };
  };

  /**
   * Resets the analytics context
   */
  const resetAnalyticsContext = () => {
    analyticsContext.eventAttributes = {};
    analyticsContext.eventPrefix = '';
  };

  if (initialEventAttributes) {
    addEventAttributes(initialEventAttributes);
  }

  if (eventPrefix) {
    analyticsContext.eventPrefix = eventPrefix;
  }

  return {
    track: trackEvent,
    addEventAttributes,
    resetAnalyticsContext,
    context: analyticsContext,
  };
};

export const withAnalytics = withHook(useAnalytics, 'analytics');

// Move to a file somewhere central? time for analytics to have a home?
export const analyticsConstants = {
  events: {
    CLICKED_LINK_TO_STUDENT_COURSE_PROGRESS:
      'clicked link to student course progress',
    CLICKED_SIDEBAR_SCHOOL_CLASS_LINK: 'clicked sidebar school class link',
    CLICKED_TAKE_A_TOUR: 'clicked take a tour button',
    TOGGLED_STUDENT_NAMES: 'toggled student names',
  },
};
